import React, { PropsWithChildren, ReactNode } from 'react';
import { Dialog } from 'primereact/dialog';
import clsx from 'clsx';
import CloseButton from '@components/RingilComponents/CloseButton.tsx';

/** Widths from the design; the confirm dialog is 'sm'. */
export type DialogSize = 'sm' | 'md' | 'lg';

const sizeClass = (size: DialogSize) =>
  clsx({
    'w-[500px] max-w-full': size === 'sm',
    'w-[700px] max-w-full': size === 'md',
    'w-[900px] max-w-full': size === 'lg',
  });

interface ComponentProps {
  visible: boolean;
  heading: string | JSX.Element;
  onHide: () => void;
  /** Rendered under the body, typically a row of RingilButtons. */
  footer?: ReactNode;
  size?: DialogSize;
  dataTestId?: string;
}

/**
 * The dialog shell from the design system: a base-coloured card with a 24px rhythm, a plain
 * headline with an orange close cross, the body, and a left-aligned action row.
 *
 * PrimeReact's own header/footer chrome is switched off (`showHeader`) and the theme's white
 * content background is overridden, so everything inside is ours — that is what keeps the
 * spacing identical to the Figma frame instead of inheriting the legacy `dialog-confirm` styles.
 */
const RingilDialog: React.FC<PropsWithChildren<ComponentProps>> = ({ visible, heading, onHide, footer, size = 'sm', dataTestId, children }) => {
  return (
    /* @ts-ignore */
    <Dialog
      visible={visible}
      onHide={onHide}
      showHeader={false}
      baseZIndex={1000000}
      closeOnEscape={true}
      dismissableMask={true}
      className={clsx('!rounded-lg !shadow-card2', sizeClass(size))}
      contentClassName={'!rounded-lg !bg-base-base !p-0'}
      maskClassName={'!bg-[var(--ringil-overlay-color)]'}
      // The mask still dims the whole screen, but padding it to the content column makes the
      // dialog centre on the content rather than on the viewport (the menu would skew it left).
      maskStyle={{ paddingLeft: 'var(--app-content-left, 0px)', paddingRight: 'calc(100vw - var(--app-content-right, 100vw))' }}
    >
      <div className={'flex flex-col gap-6 p-6'} data-testid={dataTestId}>
        <div className={'flex flex-row items-center gap-4'}>
          <div className={'min-w-0 flex-1 break-words text-2xl leading-normal tracking-[-0.48px] text-content-primary'}>{heading}</div>
          <CloseButton onClick={() => onHide()} />
        </div>
        {children}
        {footer && <div className={'flex flex-row items-center gap-6'}>{footer}</div>}
      </div>
    </Dialog>
  );
};

export default RingilDialog;
